import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import {
  FaArrowDown,
  FaArrowUp,
  FaCopy,
  FaEdit,
  FaEnvelope,
  FaFilePdf,
  FaLink,
  FaLock,
  FaPlus,
  FaSave,
  FaTimes,
  FaTrash,
  FaWallet,
} from "react-icons/fa";
import toast from "react-hot-toast";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import useHRToolsAccess from "../hooks/useHRToolsAccess";
import OffboardingDocumentsPanel from "./OffboardingDocumentsPanel";
import styles from "./HRTools.module.css";

const LINKS_COLLECTION = "hr_tools_links";
const EMAILS_COLLECTION = "hr_tools_emails";
const SUBSCRIPTIONS_COLLECTION = "hr_tools_subscriptions";
const HR_TOOLS_SEED_VERSION = 2;

const DEFAULT_LINKS = [
  {
    id: "nipun-personal-folder",
    order: 1,
    title: "Nipun's personal folder",
    description: "Personal folder shared for important docs and quick HR references.",
    url: "https://drive.google.com/drive/folders/1QZYqoZZhWzwtjmqtbK9Si6yHorixSt-m",
    bgColor: "#e0f2fe",
    textColor: "#0f172a",
  },
  {
    id: "dhairya-personal-folder",
    order: 2,
    title: "Dhairya Personal folders",
    description: "Personal folder shared for important docs and quick HR references.",
    url: "https://drive.google.com/drive/folders/13szpKZn18QAVMxVFUL1gVYehA5UAK_Tl",
    bgColor: "#dcfce7",
    textColor: "#14532d",
  },
];

const DEFAULT_EMAILS = [
  {
    id: "progress-sync-om-sir",
    order: 1,
    title: "Progress Sync and Mentorship Session",
    subject: "# Our Privilege: Progress Sync & Mentorship Session with Om Tiwari Sir!",
    body: `Hi {{name}},\n\nHope you are having a great weekend.\n\nI'm writing to let you know that we have a fantastic opportunity today. **Om Tiwari Sir** has kindly agreed to take some time out to meet with us!\n\nAs you know, having Sir's mentorship is an incredible privilege for all of us. The main goal of this meeting is to catch him up on our progress, get his expert guidance on our trajectory, and honestly, just soak in some of that invaluable motivation.\n\n**Here are the details for meeting:**\n\n- **Date:** {{date}}\n- **Time:** 02:30 PM (IST)\n- **Meeting Link:** https://meet.google.com/otv-qmfy-ykz\n\nTo make the absolute most of this session, **please do a quick bit of homework before we jump in.** Review our current milestones, be completely honest about where our status stands, and identify any areas where you feel we are currently lagging or lacking. This is a safe space to ask anything, so don't hesitate to bring your real challenges to the table.\n\nLet's utilize this exciting opportunity to learn and gain as much as we can from his expertise.\n\nLooking forward to a great session.`,
    bgColor: "#fff7ed",
    textColor: "#7c2d12",
  },
  {
    id: "offer-letter-welcome",
    order: 2,
    title: "Offer Letter Welcome Email",
    subject: "# Welcome to the Team, {{name}}! | Offer Letter from PrrepX Pathshala",
    body: `Dear **{{name}}**,\n\n**Huge congratulations on your final selection!**\n\nWe are absolutely delighted to formally welcome you to the **PrrepX Pathshala** family. Your expertise and passion truly impressed us, and we are excited to see the energy and knowledge you will bring to our academic team. This is the beginning of a fantastic journey together!\n\nPlease find your official offer letter attached to this email. It outlines your compensation, benefits, and the terms of your new role with us.\n\n---\n\n### Your Next Steps\n\n- **Review:** Please go through the attached document carefully.\n- **Sign & Submit:** Please sign the offer letter and return it with the documents mentioned in **Section 19 (f) on Page 26**.\n- **Our Shared Commitment:** By signing this offer, you agree to a two-year association with us as per our standard bond policy.\n- **Deadline:** Please revert with your signed copy and documents by **{{date}}**.\n\n---\n\nWe are genuinely thrilled to have you on board. We believe your contribution will be instrumental in shaping the future of our students, and we cannot wait to see you excel here.\n\nWishing you a bright, successful, and rewarding career with us. **Welcome aboard!**\n\nPFA. (Offer Letter PDF)`,
    bgColor: "#ecfeff",
    textColor: "#164e63",
  },
  {
    id: "written-exam-test-link",
    order: 3,
    title: "Written Exam Test Link",
    subject: "# Written Exam Test Link - Chemistry Faculty Hiring Round 1 | PRREPX PATHSHALA PVT. LTD.",
    body: `Dear Applicant,\n\nYou may begin your test immediately. Please read all instructions carefully before starting.\n\n**Important Guidelines:**\n\n- Tab switching is strictly prohibited.\n- The test is fully proctored, and the entire session will be recorded, including periodic screenshots.\n- Any form of misconduct (cheating, tab switching, or violation of instructions) will result in immediate disqualification.\n\n**Test Link:** https://www.prrepxpathshala.live/test/chemistry-hiring\n\nNote that test link will expire exactly at 4:05 PM and only one attempt is allowed.\n\nBest wishes for your assessment.`,
    bgColor: "#eff6ff",
    textColor: "#1e3a8a",
  },
  {
    id: "thank-you-for-applying",
    order: 4,
    title: "Thank You for Applying",
    subject: "Thank You for Applying to PrrepX Pathshala",
    body: `Hello {{name}},\n\nThank you for your interest in **PrrepX Pathshala**. We are delighted to connect with you and truly appreciate the time and intent you have shown by applying for the Physics/Chemistry faculty position. We look forward to the possibility of building something impactful together.\n\nAt **PrrepX Pathshala**, we are shaping India's most powerful and inspirational learning ecosystem - where educators are from **IITs, NITs, and top medical colleges**.\n\nOur vision is simple: **to build an edtech where every teacher is valued, respected, supported, and grows faster than anywhere else in the industry.**\n\nAt the moment, our team is reviewing your application along with others. We will share the confirmed schedule for the upcoming hiring rounds shortly.\n\nMeanwhile, one of our team members will connect with you via **WhatsApp or a phone call** for a brief introduction.\n\nThank you for your patience and for considering PrrepX Pathshala as the next step in your teaching journey.\n\nWe wish you the very best and look forward to speaking with you soon.`,
    bgColor: "#fef3c7",
    textColor: "#78350f",
  },
  {
    id: "dummy-follow-up",
    order: 5,
    title: "Dummy Follow-up Email",
    subject: "Quick Follow-up on Your Application",
    body: `Hi {{name}},\n\nThis is a friendly follow-up regarding your application status as of {{date}}.\n\nOur team is currently reviewing profiles and will update you soon.\n\nRegards,\nPrrepX Pathshala HR Team`,
    bgColor: "#f3e8ff",
    textColor: "#581c87",
  },
];

const DEFAULT_SUBSCRIPTIONS = [
  {
    id: "google-workspace",
    order: 1,
    title: "Google Workspace",
    details: "Company email and productivity suite subscription.",
    nextRenewDate: "2026-08-01",
    bgColor: "#eef2ff",
    textColor: "#312e81",
  },
  {
    id: "canva-pro",
    order: 2,
    title: "Canva Pro",
    details: "Design subscription used for social creatives and docs.",
    nextRenewDate: "2026-09-15",
    bgColor: "#ecfeff",
    textColor: "#155e75",
  },
  {
    id: "zoom-business",
    order: 3,
    title: "Zoom Business",
    details: "Meeting and webinar subscription for interviews and mentoring.",
    nextRenewDate: "2026-10-05",
    bgColor: "#f0fdf4",
    textColor: "#14532d",
  },
];

const getEmptyForm = (type) => {
  if (type === "links") {
    return {
      title: "",
      description: "",
      url: "",
      bgColor: "#e2e8f0",
      textColor: "#0f172a",
    };
  }
  if (type === "emails") {
    return {
      title: "",
      subject: "",
      body: "",
      bgColor: "#f1f5f9",
      textColor: "#0f172a",
    };
  }
  return {
    title: "",
    details: "",
    nextRenewDate: "",
    bgColor: "#f1f5f9",
    textColor: "#0f172a",
  };
};

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  return 0;
};

const sortItemsByOrder = (items = []) => {
  return [...items].sort((a, b) => {
    const orderA = Number.isFinite(a?.order) ? a.order : Number.MAX_SAFE_INTEGER;
    const orderB = Number.isFinite(b?.order) ? b.order : Number.MAX_SAFE_INTEGER;

    if (orderA !== orderB) return orderA - orderB;

    const createdA = toMillis(a?.createdAt);
    const createdB = toMillis(b?.createdAt);
    if (createdA !== createdB) return createdA - createdB;

    return (a?.title || "").localeCompare(b?.title || "");
  });
};

const HRTools = () => {
  const { userData } = useAuth();
  const { hasAccess, loading: accessLoading } = useHRToolsAccess();

  const [activeTab, setActiveTab] = useState("links");
  const [links, setLinks] = useState([]);
  const [emails, setEmails] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formType, setFormType] = useState("links");
  const [formData, setFormData] = useState(getEmptyForm("links"));

  const [previewName, setPreviewName] = useState("Nipun");
  const [previewDate, setPreviewDate] = useState(new Date().toISOString().slice(0, 10));

  const sortedLinks = useMemo(() => sortItemsByOrder(links), [links]);
  const sortedEmails = useMemo(() => sortItemsByOrder(emails), [emails]);
  const sortedSubscriptions = useMemo(() => sortItemsByOrder(subscriptions), [subscriptions]);

  const getCollectionName = (type) => {
    if (type === "links") return LINKS_COLLECTION;
    if (type === "emails") return EMAILS_COLLECTION;
    return SUBSCRIPTIONS_COLLECTION;
  };

  const getOrderedItems = (type) => {
    if (type === "links") return sortedLinks;
    if (type === "emails") return sortedEmails;
    return sortedSubscriptions;
  };

  const getNextOrder = (type) => {
    const orderedItems = getOrderedItems(type);
    if (!orderedItems.length) return 1;

    const maxOrder = orderedItems.reduce((max, item) => {
      const value = Number.isFinite(item?.order) ? item.order : 0;
      return Math.max(max, value);
    }, 0);

    return maxOrder + 1;
  };

  const syncSeedCollection = async (collectionName, items) => {
    await Promise.all(
      items.map(async (item) => {
        const ref = doc(db, collectionName, item.id);
        const snap = await getDoc(ref);
        const existingVersion = snap.exists() ? snap.data()?.seedVersion || 0 : 0;

        if (!snap.exists() || existingVersion < HR_TOOLS_SEED_VERSION) {
          await setDoc(ref, {
            ...item,
            seedVersion: HR_TOOLS_SEED_VERSION,
            createdAt: snap.exists() ? (snap.data()?.createdAt || serverTimestamp()) : serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: snap.exists() ? (snap.data()?.createdBy || userData.uid) : userData.uid,
            updatedBy: userData.uid,
          }, { merge: true });
        }
      })
    );
  };

  useEffect(() => {
    const syncSeedData = async () => {
      if (userData?.role !== "superadmin") return;

      try {
        await syncSeedCollection(LINKS_COLLECTION, DEFAULT_LINKS);
        await syncSeedCollection(EMAILS_COLLECTION, DEFAULT_EMAILS);
        await syncSeedCollection(SUBSCRIPTIONS_COLLECTION, DEFAULT_SUBSCRIPTIONS);
      } catch (err) {
        console.error("Error syncing HR Tools seed data:", err);
      }
    };

    syncSeedData();

    let linksLoaded = false;
    const markLinksLoaded = () => {
      if (!linksLoaded) {
        linksLoaded = true;
        setLoading(false);
      }
    };

    const linksQ = query(collection(db, LINKS_COLLECTION), orderBy("createdAt", "asc"));
    const emailsQ = query(collection(db, EMAILS_COLLECTION), orderBy("createdAt", "asc"));
    const subscriptionsQ = query(collection(db, SUBSCRIPTIONS_COLLECTION), orderBy("createdAt", "asc"));

    const unsubLinks = onSnapshot(
      linksQ,
      (snap) => {
        setLinks(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        markLinksLoaded();
      },
      (err) => {
        console.error("Error loading HR links:", err);
        toast.error("Failed to load important links.");
        markLinksLoaded();
      }
    );

    const unsubEmails = onSnapshot(
      emailsQ,
      (snap) => {
        setEmails(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.error("Error loading HR emails:", err);
        toast.error("Failed to load standard emails.");
      }
    );

    const unsubSubscriptions = onSnapshot(
      subscriptionsQ,
      (snap) => {
        setSubscriptions(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.error("Error loading subscriptions:", err);
        toast.error("Failed to load subscriptions.");
      }
    );

    return () => {
      unsubLinks();
      unsubEmails();
      unsubSubscriptions();
    };
  }, [userData]);

  const sectionMeta = useMemo(
    () => ({
      links: {
        title: "Important Links",
        description: "Save and manage important HR and operations links in one place.",
        icon: <FaLink />,
      },
      emails: {
        title: "Standard Emails",
        description: "Reusable templates with placeholders like {{name}} and {{date}}.",
        icon: <FaEnvelope />,
      },
      subscriptions: {
        title: "Subscriptions",
        description: "Track tools and next renewal dates.",
        icon: <FaWallet />,
      },
      offboarding: {
        title: "Offboard Documents",
        description: "Generate documents for resigned employees.",
        icon: <FaFilePdf />,
      },
    }),
    []
  );

  const openAddModal = (type) => {
    setFormType(type);
    setEditingId(null);
    setFormData(getEmptyForm(type));
    setShowModal(true);
  };

  const openEditModal = (type, item) => {
    setFormType(type);
    setEditingId(item.id);
    setFormData({ ...item });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
  };

  const updateFormField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const saveItem = async (e) => {
    e.preventDefault();

    const collectionName =
      formType === "links"
        ? LINKS_COLLECTION
        : formType === "emails"
          ? EMAILS_COLLECTION
          : SUBSCRIPTIONS_COLLECTION;

    const payload = {
      ...formData,
      updatedAt: serverTimestamp(),
      updatedBy: userData?.uid || null,
    };

    try {
      if (editingId) {
        await updateDoc(doc(db, collectionName, editingId), payload);
        toast.success("Entry updated successfully.");
      } else {
        await addDoc(collection(db, collectionName), {
          ...payload,
          order: getNextOrder(formType),
          createdAt: serverTimestamp(),
          createdBy: userData?.uid || null,
        });
        toast.success("Entry added successfully.");
      }
      closeModal();
    } catch (err) {
      console.error("Save HR Tools item error:", err);
      toast.error("Unable to save. Please try again.");
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm("Delete this item?")) return;

    const collectionName =
      type === "links"
        ? LINKS_COLLECTION
        : type === "emails"
          ? EMAILS_COLLECTION
          : SUBSCRIPTIONS_COLLECTION;

    try {
      await deleteDoc(doc(db, collectionName, id));
      toast.success("Item deleted.");
    } catch (err) {
      console.error("Delete HR Tools item error:", err);
      toast.error("Unable to delete. Please try again.");
    }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied.`);
    } catch (err) {
      console.error("Clipboard error:", err);
      toast.error("Copy failed. Please copy manually.");
    }
  };

  const moveItem = async (type, id, direction) => {
    const orderedItems = getOrderedItems(type);
    const currentIndex = orderedItems.findIndex((item) => item.id === id);
    if (currentIndex === -1) return;

    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= orderedItems.length) return;

    const reordered = [...orderedItems];
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    const collectionName = getCollectionName(type);
    const batch = writeBatch(db);
    let changedCount = 0;

    reordered.forEach((item, index) => {
      const nextOrder = index + 1;
      if (item.order !== nextOrder) {
        batch.update(doc(db, collectionName, item.id), {
          order: nextOrder,
          updatedAt: serverTimestamp(),
          updatedBy: userData?.uid || null,
        });
        changedCount += 1;
      }
    });

    if (!changedCount) return;

    try {
      await batch.commit();
      toast.success("Order updated.");
    } catch (err) {
      console.error("Reorder error:", err);
      toast.error("Unable to change order.");
    }
  };

  const applyPlaceholders = (text) =>
    (text || "")
      .replaceAll("{{name}}", previewName || "[Name]")
      .replaceAll("{{date}}", previewDate || "[Date]");

  const renderHeader = (type) => (
    <div className={styles.sectionHeader}>
      <div>
        <h2>{sectionMeta[type].title}</h2>
        <p>{sectionMeta[type].description}</p>
      </div>
      <button className={styles.addBtn} onClick={() => openAddModal(type)}>
        <FaPlus /> Add New
      </button>
    </div>
  );

  if (accessLoading || loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingBox}>Loading PrrepX Standard...</div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className={styles.page}>
        <div className={styles.lockedCard}>
          <FaLock className={styles.lockIcon} />
          <h2>PrrepX Standard Access Needed</h2>
          <p>
            This page is available to superadmin and admins who are granted PrrepX Standard access
            from Access Management.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.topBanner}>
        <h1>PrrepX Standard</h1>
        <p>Centralized links, standard emails, and subscription tracking for HR workflows.</p>
      </div>

      <div className={styles.tabBar}>
        {Object.keys(sectionMeta).map((tab) => (
          <button
            key={tab}
            className={`${styles.tabBtn} ${activeTab === tab ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {sectionMeta[tab].icon} {sectionMeta[tab].title}
          </button>
        ))}
      </div>

      {activeTab === "links" && (
        <section className={styles.sectionWrap}>
          {renderHeader("links")}
          <div className={styles.docList}>
            {sortedLinks.map((item, index) => (
              <article
                key={item.id}
                className={styles.docRow}
                style={{ backgroundColor: item.bgColor || "#f8fafc", color: item.textColor || "#0f172a" }}
              >
                <div className={styles.docMain}>
                  <h3 className={styles.docTitle}><span className={styles.rowNumber}>{index + 1}.</span> {item.title}</h3>
                  <p className={styles.docDescription}>{item.description}</p>
                  <div className={styles.literalLinkBox}>
                    <a href={item.url} target="_blank" rel="noreferrer" className={styles.literalLink}>
                      {item.url}
                    </a>
                  </div>
                </div>

                <div className={styles.rowActions}>
                  <button onClick={() => moveItem("links", item.id, "up")} title="Move up" disabled={index === 0}><FaArrowUp /></button>
                  <button onClick={() => moveItem("links", item.id, "down")} title="Move down" disabled={index === sortedLinks.length - 1}><FaArrowDown /></button>
                  <button onClick={() => copyText(item.url, "Link")} title="Copy link"><FaCopy /></button>
                  <button onClick={() => openEditModal("links", item)} title="Edit"><FaEdit /></button>
                  <button onClick={() => handleDelete("links", item.id)} title="Delete"><FaTrash /></button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {activeTab === "emails" && (
        <section className={styles.sectionWrap}>
          {renderHeader("emails")}

          <div className={styles.placeholderBar}>
            <label>
              Preview Name
              <input
                type="text"
                value={previewName}
                onChange={(e) => setPreviewName(e.target.value)}
                placeholder="Name"
              />
            </label>
            <label>
              Preview Date
              <input
                type="date"
                value={previewDate}
                onChange={(e) => setPreviewDate(e.target.value)}
              />
            </label>
          </div>

          <div className={styles.docList}>
            {sortedEmails.map((item, index) => {
              const mergedSubject = applyPlaceholders(item.subject);
              const mergedBody = applyPlaceholders(item.body);
              const copyPayload = `Subject: ${mergedSubject}\n\n${mergedBody}`;

              return (
                <article
                  key={item.id}
                  className={styles.docRow}
                  style={{ backgroundColor: item.bgColor || "#f8fafc", color: item.textColor || "#0f172a" }}
                >
                  <div className={styles.docMain}>
                    <h3 className={styles.docTitle}><span className={styles.rowNumber}>{index + 1}.</span> {item.title}</h3>
                    <p className={styles.subjectLine}><strong>Subject:</strong> {mergedSubject}</p>
                    <pre className={styles.emailBody}>{mergedBody}</pre>
                  </div>

                  <div className={styles.rowActions}>
                    <button onClick={() => moveItem("emails", item.id, "up")} title="Move up" disabled={index === 0}><FaArrowUp /></button>
                    <button onClick={() => moveItem("emails", item.id, "down")} title="Move down" disabled={index === sortedEmails.length - 1}><FaArrowDown /></button>
                    <button onClick={() => copyText(copyPayload, "Email template")} title="Copy email"><FaCopy /></button>
                    <button onClick={() => openEditModal("emails", item)} title="Edit"><FaEdit /></button>
                    <button onClick={() => handleDelete("emails", item.id)} title="Delete"><FaTrash /></button>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {activeTab === "subscriptions" && (
        <section className={styles.sectionWrap}>
          {renderHeader("subscriptions")}
          <div className={styles.docList}>
            {sortedSubscriptions.map((item, index) => (
              <article
                key={item.id}
                className={styles.docRow}
                style={{ backgroundColor: item.bgColor || "#f8fafc", color: item.textColor || "#0f172a" }}
              >
                <div className={styles.docMain}>
                  <h3 className={styles.docTitle}><span className={styles.rowNumber}>{index + 1}.</span> {item.title}</h3>
                  <p className={styles.docDescription}>{item.details}</p>
                  <p className={styles.renewLine}>Next Renew: {item.nextRenewDate || "Not set"}</p>
                </div>

                <div className={styles.rowActions}>
                  <button onClick={() => moveItem("subscriptions", item.id, "up")} title="Move up" disabled={index === 0}><FaArrowUp /></button>
                  <button onClick={() => moveItem("subscriptions", item.id, "down")} title="Move down" disabled={index === sortedSubscriptions.length - 1}><FaArrowDown /></button>
                  <button onClick={() => openEditModal("subscriptions", item)} title="Edit"><FaEdit /></button>
                  <button onClick={() => handleDelete("subscriptions", item.id)} title="Delete"><FaTrash /></button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {activeTab === "offboarding" && <OffboardingDocumentsPanel />}

      {showModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{editingId ? "Edit Entry" : "Add Entry"}</h3>
              <button onClick={closeModal}><FaTimes /></button>
            </div>

            <form className={styles.modalForm} onSubmit={saveItem}>
              <label>
                Title
                <input
                  type="text"
                  value={formData.title || ""}
                  onChange={(e) => updateFormField("title", e.target.value)}
                  required
                />
              </label>

              {formType === "links" && (
                <>
                  <label>
                    Description
                    <textarea
                      value={formData.description || ""}
                      onChange={(e) => updateFormField("description", e.target.value)}
                      rows={3}
                    />
                  </label>
                  <label>
                    URL
                    <input
                      type="url"
                      value={formData.url || ""}
                      onChange={(e) => updateFormField("url", e.target.value)}
                      required
                    />
                  </label>
                </>
              )}

              {formType === "emails" && (
                <>
                  <label>
                    Subject
                    <input
                      type="text"
                      value={formData.subject || ""}
                      onChange={(e) => updateFormField("subject", e.target.value)}
                      required
                    />
                  </label>
                  <label>
                    Body
                    <textarea
                      value={formData.body || ""}
                      onChange={(e) => updateFormField("body", e.target.value)}
                      rows={10}
                      required
                    />
                  </label>
                </>
              )}

              {formType === "subscriptions" && (
                <>
                  <label>
                    Details
                    <textarea
                      value={formData.details || ""}
                      onChange={(e) => updateFormField("details", e.target.value)}
                      rows={4}
                      required
                    />
                  </label>
                  <label>
                    Next Renewal Date
                    <input
                      type="date"
                      value={formData.nextRenewDate || ""}
                      onChange={(e) => updateFormField("nextRenewDate", e.target.value)}
                      required
                    />
                  </label>
                </>
              )}

              <div className={styles.colorRow}>
                <label>
                  Background Color
                  <input
                    type="color"
                    value={formData.bgColor || "#f1f5f9"}
                    onChange={(e) => updateFormField("bgColor", e.target.value)}
                  />
                </label>
                <label>
                  Text Color
                  <input
                    type="color"
                    value={formData.textColor || "#0f172a"}
                    onChange={(e) => updateFormField("textColor", e.target.value)}
                  />
                </label>
              </div>

              <button type="submit" className={styles.saveBtn}>
                <FaSave /> {editingId ? "Save Changes" : "Add Entry"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HRTools;
